import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { fetchTranslations } from "../types/translationsClients";
import DocklineFX from "../components/DocklineFX";
import HeroFX from "../components/HeroFX";
import ContactComponent from "../components/ContactComponent";
import { Helmet } from 'react-helmet-async';

const WebentwicklerMuenchen = () => {
    const [dict, setDict] = useState<Record<string, string> | null>(null);
    const locale = "de";
    const namespace = "landing";

    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                const data = await fetchTranslations({
                    baseUrl: process.env.REACT_APP_API_BASE_URL || "",
                    locale,
                    namespace
                });
                if (!cancelled) setDict(data.items);
            } catch (e) {
                if (!cancelled) setDict({});
                console.error("Failed to load translations (landing)", e);
            }
        })();
        return () => {
            cancelled = true;
        };
    }, []);

    // Reuse hero parallax / tilt
    useEffect(() => {
        const hero = document.querySelector('.landing-hero-cover') as HTMLElement | null;
        if (!hero) return;

        const clamp = (v: number, min = 0, max = 1) => Math.min(max, Math.max(min, v));

        let rafId = 0;
        const loop = () => {
            const doc = document.documentElement;
            const st = doc.scrollTop || document.body.scrollTop || 0;

            const maxScroll = Math.max(1, doc.scrollHeight - doc.clientHeight);
            const hideThreshold = 0.5 * maxScroll;

            if (st >= hideThreshold) {
                hero.classList.add('lh-hidden');
            } else {
                hero.classList.remove('lh-hidden');

                const vh = window.innerHeight || 1;
                const t = clamp(st / (vh * 1.4));

                const scale = 1 - 0.20 * t;
                const tilt = 10 * t;

                hero.style.transform = `translateX(-50%) perspective(1000px) scale(${scale}) rotateX(${tilt}deg)`;
            }

            rafId = requestAnimationFrame(loop);
        };

        rafId = requestAnimationFrame(loop);
        return () => {
            hero.classList.remove('lh-hidden');
            cancelAnimationFrame(rafId);
        };
    }, []);


    return (
        <>
            <Helmet>
                <title>Webentwickler in München | Dockline Development</title>
                <meta
                    name="description"
                    content="Ihr Webentwickler in München: Dockline Development entwickelt moderne Websites, Landingpages, Webanwendungen und individuelle Softwarelösungen für Unternehmen in und um München."
                />
                <meta
                    name="keywords"
                    content="Webentwickler München, Webentwicklung München, Softwareentwicklung München, Full Stack Developer München, Web Developer München, App Entwicklung München, Dockline Development"
                />
                <link rel="canonical" href="https://dockline.digital/webentwickler-muenchen" />

                {/* Open Graph */}
                <meta property="og:type" content="website" />
                <meta property="og:title" content="Webentwickler in München – Dockline Development" />
                <meta
                    property="og:description"
                    content="Webentwicklung, Full-Stack Engineering und individuelle Software in München. Dockline Development – Ihr Webentwickler für performante, skalierbare Lösungen."
                />
                <meta property="og:url" content="https://dockline.digital/webentwickler-muenchen" />
                <meta property="og:image" content="https://dockline.digital/pics/og/devs-og.jpg" />
                <meta property="og:locale" content="de_DE" />

                {/* JSON-LD: Local web development service in Munich */}
                <script type="application/ld+json">{`
                {
                  "@context": "https://schema.org",
                  "@type": "ProfessionalService",
                  "name": "Webentwickler in München – Dockline Development",
                  "url": "https://dockline.digital/webentwickler-muenchen",
                  "logo": "https://dockline.digital/pics/logo/sec/ddevs_logo_sec.svg",
                  "serviceType": "Webentwicklung & Softwareentwicklung in München",
                  "description": "Dockline Development ist Ihr Webentwickler in München für Websites, Webanwendungen und individuelle Softwarelösungen.",
                  "areaServed": [
                    {
                      "@type": "City",
                      "name": "München",
                      "addressCountry": "DE"
                    },
                    {
                      "@type": "Country",
                      "name": "Deutschland"
                    }
                  ],
                  "provider": {
                    "@type": "Organization",
                    "name": "Dockline Digital"
                  }
                }
                `}</script>
                {/* JSON-LD FAQ schema */}
                <script type="application/ld+json">{`
                {
                  "@context": "https://schema.org",
                  "@type": "FAQPage",
                  "mainEntity": [
                    {
                      "@type": "Question",
                      "name": "Welche Arten von Projekten übernimmt Dockline Development in München?",
                      "acceptedAnswer": {
                        "@type": "Answer",
                        "text": "Dockline Development entwickelt individuelle Websites und Landingpages, Webanwendungen, interne Tools, Dashboards und Schnittstellenlösungen für Unternehmen im Raum München. Im Fokus stehen saubere, wartbare Lösungen, die konkrete Geschäftsprozesse unterstützen."
                      }
                    },
                    {
                      "@type": "Question",
                      "name": "Arbeiten Sie nur mit Unternehmen in München oder auch deutschlandweit?",
                      "acceptedAnswer": {
                        "@type": "Answer",
                        "text": "Wir sind in München ansässig, arbeiten aber mit Unternehmen in ganz Deutschland zusammen. Viele Webentwicklungsprojekte laufen vollständig remote – mit Abstimmung per Video-Call und Online-Tools –, sodass Sie nicht vor Ort sein müssen."
                      }
                    },
                    {
                      "@type": "Question",
                      "name": "Wie werden Webentwicklung und Softwareentwicklung abgerechnet?",
                      "acceptedAnswer": {
                        "@type": "Answer",
                        "text": "Webentwicklung und Softwareentwicklung werden in der Regel auf Stundenbasis abgerechnet. Im ersten Briefing-Gespräch klären wir Ziele und Rahmenbedingungen und geben eine realistische Aufwandsschätzung, damit Sie die zu erwartende Größenordnung kennen, bevor wir starten."
                      }
                    },
                    {
                      "@type": "Question",
                      "name": "Entwickeln Sie auch kleinere Websites oder Landingpages für Selbstständige?",
                      "acceptedAnswer": {
                        "@type": "Answer",
                        "text": "Ja. Neben größeren Projekten entwickeln wir auch schlanke Websites und Landingpages für Selbstständige, Kanzleien, Praxen und kleinere Unternehmen in und um München. Wichtig ist, dass das Ergebnis zu Ihrem Auftritt und Ihrem Budget passt."
                      }
                    },
                    {
                      "@type": "Question",
                      "name": "Können Sie mit bestehenden Systemen oder Legacy-Code arbeiten?",
                      "acceptedAnswer": {
                        "@type": "Answer",
                        "text": "Viele Projekte entstehen auf Basis bestehender Systeme. Wir können vorhandenen Code übernehmen, Schnittstellen zu Drittsystemen entwickeln oder Legacy-Anwendungen Schritt für Schritt stabilisieren und modernisieren, statt alles auf einmal neu zu bauen."
                      }
                    },
                    {
                      "@type": "Question",
                      "name": "Übernehmen Sie auch Hosting, Deployment und Wartung?",
                      "acceptedAnswer": {
                        "@type": "Answer",
                        "text": "Auf Wunsch unterstützen wir bei Hosting, Deployment, Monitoring und Updates oder arbeiten mit Ihrem bestehenden IT-Team zusammen. Gemeinsam wählen wir geeignete Infrastruktur, richten automatisierte Deployments ein und definieren, wie Ihre Anwendung langfristig gepflegt wird."
                      }
                    },
                    {
                      "@type": "Question",
                      "name": "Wie startet ein typisches Webprojekt?",
                      "acceptedAnswer": {
                        "@type": "Answer",
                        "text": "Ein typisches Projekt beginnt mit einem kurzen Kennenlern- und Briefing-Gespräch. Daraus entsteht ein Vorschlag mit Zielen, grobem Scope, Milestones und einer Aufwandsschätzung. Nach Ihrer Freigabe folgen Feinkonzept, Design und die eigentliche Implementierung."
                      }
                    }
                  ]
                }
                `}</script>
            </Helmet>

            <div style={{'--fx-color': '#5C33F6', '--fx-glow': 'rgba(92,51,246,0.35)'} as React.CSSProperties}>
                <div className="landing-hero-cover hero-anim">
                    <img src="/pics/landingPref3Dev.webp" alt="" className="landing-hero" id="landing-hero" />
                    <HeroFX/>
                    <div className="landing-hero-centered-text">
                        <h1 className="dev-color">Webentwickler in München</h1>
                        <h2>Dockline Development – Full-Stack Web &amp; Software</h2>
                    </div>
                    <a
                        className="landing-hero-bottom-hint"
                        onClick={() => window.scrollTo({ top: window.innerHeight, behavior: 'smooth' })}
                        style={{ cursor: 'pointer' }}
                    >
                        Scroll to Explore
                    </a>
                </div>

                <div className="content">
                    <DocklineFX color="#5C33F6"/>

                    {/* Intro tailored to Munich */}
                    <section className="devs-intro">
                        <div className="section-intro">
                            <h2 className="dev-color">Webentwicklung für München und Umgebung</h2>
                            <p>
                                Dockline Development ist Ihr <strong>Webentwickler in München</strong> für
                                <strong> Webentwicklung und Softwareentwicklung</strong>. Wir entwickeln schnelle
                                Websites und Landingpages, robuste Webanwendungen und individuelle Softwarelösungen
                                für Unternehmen, Kanzleien, Praxen und Startups im Raum München.
                            </p>
                        </div>
                    </section>

                    {/* === Vibe-to-Production Banner === */}
                    <section className="vibe-banner">
                        <div className="vibe-banner__eyebrow">Neu</div>
                        <h2>Vibe Coding war erst der Anfang. <span>Jetzt bauen wir's richtig.</span></h2>
                        <p>
                            Sie haben Ihre Idee mit KI-Tools zum Leben erweckt – ein guter erster Schritt.
                            Jetzt ist der Moment, einen echten Entwickler zu engagieren: saubere Architektur,
                            echte Sicherheit, Software die skaliert und langfristig wartbar bleibt.
                        </p>
                        <Link to="/" state={{scrollTo: 'contact-section'}}>
                            <button><p>Prototyp besprechen</p></button>
                        </Link>
                    </section>

                    {/* What we build – localized copy */}
                    <section className="devs-work" aria-labelledby="devs-work-title">
                        <div className="section-intro">
                            <h2 id="devs-work-title">Webentwicklung &amp; Softwareentwicklung aus München</h2>
                            <p>
                                Von der schlanken Website bis zur komplexen Business-Anwendung – wir setzen Projekte
                                um, die zu Ihrem Team und Ihrem Wachstum passen.
                            </p>
                        </div>
                        <div className="devs-work__cards">
                            <article className="devs-card">
                                <i className="bi bi-window"></i>
                                <h3>Websites &amp; Landingpages</h3>
                                <p>
                                    Individuell gestaltete Websites und Landingpages mit sauberer Code-Basis,
                                    SEO-Grundoptimierung und einfacher Pflege – ideal für Unternehmen und
                                    Selbstständige in und um München.
                                </p>
                            </article>

                            <article className="devs-card">
                                <i className="bi bi-braces"></i>
                                <h3>Webanwendungen &amp; Tools</h3>
                                <p>
                                    Interne Tools, Dashboards und Portale – Full-Stack-Entwicklung inkl. Auth,
                                    Rollenmodellen, Exporten und Integrationen in bestehende Systeme. Ideal, wenn Sie
                                    eine Webapp entwickeln lassen möchten, die genau zu Ihren Abläufen passt.
                                </p>
                            </article>

                            <article className="devs-card">
                                <i className="bi bi-diagram-3"></i>
                                <h3>APIs &amp; Integrationen</h3>
                                <p>
                                    Wir verbinden Ihre Systeme – von REST-APIs über Schnittstellen zu CRM, ERP oder
                                    Drittsystemen bis hin zu Automatisierungen mit Cloud-Diensten.
                                </p>
                            </article>
                        </div>
                    </section>

                    {/* Process – how we work together */}
                    <section className="process-snapshot" aria-labelledby="devs-process-title">
                        <div className="section-intro">
                            <h2 id="devs-process-title">So arbeiten wir mit Ihnen</h2>
                            <p>
                                Wir sprechen auf Augenhöhe, planen transparent und liefern in klaren, überprüfbaren
                                Schritten – von der Idee bis zum Live-Betrieb.
                            </p>
                        </div>
                        <ol className="process-steps">
                            <li className="process-step">
                                <div className="step-badge">1</div>
                                <i className="bi bi-search"></i>
                                <h3>Analyse &amp; Scope</h3>
                                <p>
                                    Wir klären Ziele, Anforderungen und bestehende Systeme – telefonisch, remote oder
                                    vor Ort in München. So arbeiten wir wie ein externer, aber langfristig verfügbarer
                                    <strong> Webentwickler für Ihr Unternehmen in München</strong>.
                                </p>
                            </li>
                            <li className="process-step">
                                <div className="step-badge">2</div>
                                <i className="bi bi-pencil-square"></i>
                                <h3>Konzept &amp; Architektur</h3>
                                <p>
                                    Wir entwerfen UX-Flows, Datenmodell und technische Architektur – so einfach wie
                                    möglich, so robust wie nötig.
                                </p>
                            </li>
                            <li className="process-step">
                                <div className="step-badge">3</div>
                                <i className="bi bi-gear"></i>
                                <h3>Implementierung</h3>
                                <p>
                                    Iterative Entwicklung mit regelmäßigen Demos – Sie sehen früh, was entsteht, und
                                    können Feedback geben.
                                </p>
                            </li>
                            <li className="process-step">
                                <div className="step-badge">4</div>
                                <i className="bi bi-bar-chart-line"></i>
                                <h3>Launch &amp; Betrieb</h3>
                                <p>
                                    Deployment, Monitoring, Bugfixes und Erweiterungen – auf Wunsch übernehmen wir den
                                    laufenden Betrieb Ihrer Lösung.
                                </p>
                            </li>
                        </ol>
                    </section>

                    {/* References: reuse Dev references grid */}
                    <section className="devs-references" aria-labelledby="devs-references-title">
                        <div className="section-intro">
                            <h2 id="devs-references-title">Ausgewählte Projekte</h2>
                            <p>
                                Eine Auswahl von Projekten, an denen wir mitgewirkt haben – vom Website-Relaunch bis
                                zur individuellen Web-Applikation.
                            </p>
                        </div>
                        <div className="devs-ref__grid">
                            <figure className="media-frame devs-ref__item">
                                <img src="/pics/devs/clientadmin.webp" alt="Website Client Admin" />
                                <figcaption>Website Client Admin – Dockline Devs Produkt</figcaption>
                            </figure>
                            <figure className="media-frame devs-ref__item">
                                <img src="/pics/devs/crm.webp" alt="CRM Webapplikation" />
                                <figcaption>CRM – Individuelle Webanwendung</figcaption>
                            </figure>
                            <figure className="media-frame devs-ref__item">
                                <img src="/pics/devs/maike.webp" alt="Website" />
                                <figcaption>Website – Design &amp; Entwicklung</figcaption>
                            </figure>
                            <figure className="media-frame devs-ref__item">
                                <img src="/pics/devs/ddweb.webp" alt="Dockline Digital Website" />
                                <figcaption>Dockline Digital – Corporate Website</figcaption>
                            </figure>
                            <figure className="media-frame devs-ref__item">
                                <img src="/pics/devs/jrcweb.webp" alt="Executive Search Webseite" />
                                <figcaption>Jack Russell Consulting – Website</figcaption>
                            </figure>
                            <figure className="media-frame devs-ref__item">
                                <img src="/pics/devs/sarepo.webp" alt="Reporting Tool" />
                                <figcaption>Reporting Tool – Custom Web App</figcaption>
                            </figure>
                            <figure className="media-frame devs-ref__item">
                                <img src="/pics/devs/russellgpt.webp" alt="Private GPT" />
                                <figcaption>Private GPT – Individuelle AI-Applikation</figcaption>
                            </figure>
                            <figure className="media-frame devs-ref__item">
                                <img src="/pics/devs/pdfexport.webp" alt="PDF Export Tool" />
                                <figcaption>PDF Report Tool – Custom Python Anwendung</figcaption>
                            </figure>
                        </div>
                    </section>

                    {/* Stack section reused */}
                    <section className="devs-stack" aria-labelledby="devs-stack-title">
                        <div className="section-intro">
                            <h2 id="devs-stack-title">Technologien, mit denen wir arbeiten</h2>
                            <p>
                                Moderne, gut unterstützte Tools und Frameworks – ausgewählt, um Ihr Projekt langfristig
                                wartbar zu halten.
                            </p>
                        </div>
                        <div className="stack-ticker" aria-hidden="true">
                            <div className="stack-ticker__track">
                                <img src="/pics/icons/react.svg" alt="React" />
                                <img src="/pics/icons/typescript.svg" alt="TypeScript" />
                                <img src="/pics/icons/nodejs.svg" alt="Node.js" />
                                <img src="/pics/icons/php.svg" alt="PHP" />
                                <img src="/pics/icons/mysql.svg" alt="MySQL" />
                                <img src="/pics/icons/docker.svg" alt="Docker" />
                                <img src="/pics/icons/nginx.svg" alt="Nginx" />
                                <img src="/pics/icons/aws.svg" alt="AWS" />
                                <img src="/pics/icons/azure.svg" alt="Azure" />
                                <img src="/pics/icons/git.svg" alt="Git" />
                            </div>
                        </div>
                    </section>

                    {/* === Why Dockline Devs – Webentwickler in München === */}
                    <section className="systems-why" aria-labelledby="webdev-why-title">
                        <div className="section-intro">
                            <h2 id="webdev-why-title" className="dev-color">
                                Warum Dockline Devs als Webentwickler in München?
                            </h2>
                            <p>
                                Dockline Devs steht für moderne Webentwicklung mit direktem Kontakt zur entwickelnden Person.
                                Keine Agentur-Schichten, sondern ein klarer Ansprechpartner, der Ihre Anforderungen versteht
                                und selbst umsetzt.
                            </p>
                        </div>
                        <div className="systems-why__grid">
                            <article className="systems-card systems-why__item">
                                <h3>Direkter Kontakt zum Entwickler</h3>
                                <p>
                                    Sie sprechen direkt mit dem Webentwickler, der Ihr Projekt plant, umsetzt und betreut.
                                    Feedback landet ohne Umwege bei der richtigen Person – das spart Zeit, Kosten und vermeidet
                                    Missverständnisse.
                                </p>
                            </article>
                            <article className="systems-card systems-why__item">
                                <h3>Aktueller Technologie-Stack</h3>
                                <p>
                                    Als aktiver Softwareentwickler und Informatikstudent arbeite ich mit modernen Technologien
                                    wie React, TypeScript, Node.js, PHP und containerisierten Umgebungen. So entstehen
                                    performante, gut wartbare Lösungen, die zu Ihrem Unternehmen in München passen.
                                </p>
                            </article>
                            <article className="systems-card systems-why__item">
                                <h3>Schlanke Struktur, faire Projektkonditionen</h3>
                                <p>
                                    Dockline Devs arbeitet ohne großen Agentur-Overhead. Das bedeutet
                                    <em> faire, transparente Stundensätze</em> und realistische Aufwandsschätzungen – besonders
                                    interessant für kleine und mittlere Unternehmen, die professionelle
                                    <strong> Webentwicklung in München</strong> suchen, ohne Agenturpreise zu zahlen.
                                </p>
                            </article>
                        </div>
                    </section>

                    {/* === FAQ === */}
                    <section className="systems-faq" aria-labelledby="webdev-faq-title">
                        <div className="section-intro">
                            <h2 id="webdev-faq-title" className="dev-color">Häufige Fragen zur Webentwicklung in München</h2>
                            <p>
                                Antworten auf typische Fragen rund um Webentwicklung und Softwareentwicklung in München.
                                Wenn Ihre Situation hier nicht vorkommt, schreiben Sie uns kurz – wir melden uns mit
                                einer Einschätzung.
                            </p>
                        </div>
                        <div className="faq-list">
                            <details className="faq-item">
                                <summary><p className="boldText">Welche Arten von Projekten übernimmt Dockline Development in München?</p></summary>
                                <p>
                                    Wir entwickeln individuelle Websites und Landingpages, Webanwendungen, interne Tools,
                                    Dashboards und Schnittstellenlösungen. Im Fokus stehen saubere, wartbare Lösungen, die
                                    konkrete Geschäftsprozesse unterstützen – nicht möglichst viele Features auf einmal.
                                </p>
                            </details>
                            <details className="faq-item">
                                <summary><p className="boldText">Arbeiten Sie nur mit Unternehmen in München oder auch deutschlandweit?</p></summary>
                                <p>
                                    Wir sind in München ansässig, arbeiten aber mit Unternehmen in ganz Deutschland
                                    zusammen. Viele Webentwicklungsprojekte laufen vollständig remote – mit Abstimmung
                                    per Video-Call und Online-Tools –, sodass Sie nicht vor Ort sein müssen.
                                </p>
                            </details>
                            <details className="faq-item">
                                <summary><p className="boldText">Wie werden Webentwicklung und Softwareentwicklung abgerechnet?</p></summary>
                                <p>
                                    In der Regel rechnen wir nach tatsächlich angefallenen Stunden ab. Im ersten
                                    Briefing-Gespräch besprechen wir Ziele und Rahmenbedingungen und geben eine
                                    realistische Aufwandsschätzung, damit Sie die zu erwartende Größenordnung vorab
                                    kennen.
                                </p>
                            </details>
                            <details className="faq-item">
                                <summary><p className="boldText">Entwickeln Sie auch kleinere Websites oder Landingpages für Selbstständige?</p></summary>
                                <p>
                                    Ja. Neben größeren Projekten setzen wir auch fokussierte Websites und Landingpages
                                    für Selbstständige, Kanzleien, Praxen und kleinere Unternehmen um – inklusive
                                    sinnvoller SEO-Grundlagen und einfacher Pflege.
                                </p>
                            </details>
                            <details className="faq-item">
                                <summary><p className="boldText">Können Sie mit bestehenden Systemen oder Legacy-Code arbeiten?</p></summary>
                                <p>
                                    Viele Projekte entstehen auf Basis bestehender Systeme. Wir können vorhandenen Code
                                    übernehmen, Schnittstellen zu Drittsystemen bauen oder Legacy-Anwendungen Schritt
                                    für Schritt stabilisieren und modernisieren, statt alles von heute auf morgen zu
                                    ersetzen.
                                </p>
                            </details>
                            <details className="faq-item">
                                <summary><p className="boldText">Übernehmen Sie auch Hosting, Deployment und Wartung?</p></summary>
                                <p>
                                    Auf Wunsch unterstützen wir bei Hosting, Deployment, Monitoring und Updates oder
                                    arbeiten mit Ihrem bestehenden IT-Team zusammen. Gemeinsam wählen wir eine passende
                                    Infrastruktur und richten Deployments so ein, dass neue Versionen möglichst
                                    reibungslos online gehen.
                                </p>
                            </details>
                            <details className="faq-item">
                                <summary><p className="boldText">Wie startet ein typisches Webprojekt?</p></summary>
                                <p>
                                    Üblicherweise starten wir mit einem kurzen Kennenlern- und Briefing-Call. Daraus
                                    entsteht ein Vorschlag mit Zielen, grobem Scope, Milestones und einer
                                    Aufwandsschätzung. Nach Ihrer Freigabe folgen Feinkonzept, Design und die
                                    Implementierung in mehreren überprüfbaren Schritten.
                                </p>
                            </details>
                        </div>
                    </section>

                    {/* CTA: local + link to main Dev page */}
                    <section className="systems-cta">
                        <h2 className="dev-color">Webentwickler in München gesucht?</h2>
                        <p id="systems-cta-p">
                            Erzählen Sie uns von Ihrem Projekt – Website, Landingpage, Webanwendung oder
                            individuelle Softwarelösung. Als <strong>Webentwickler in München</strong> melden
                            wir uns mit einer klaren, realistischen Einschätzung.
                        </p>
                        <div style={{ display: "flex", gap: "1rem", flexWrap: "wrap", justifyContent: "center" }}>
                            <Link to="/" state={{scrollTo: 'contact-section'}}>
                                <button><p>Kontakt aufnehmen</p></button>
                            </Link>
                            <Link to="/dev">
                                <button><p>Mehr zu Dockline Development</p></button>
                            </Link>
                        </div>
                    </section>

                    {/*<section className="landing-cta-container">*/}
                    {/*    <ContactComponent />*/}
                    {/*</section>*/}
                </div>
            </div>
        </>
    );
};

export default WebentwicklerMuenchen;